import React from 'react';

import { Modal } from '../Modal/Modal';
import { useSharedTranslation } from '../hooks/useSharedTranslation';
import { ScheduleEntry } from 'us-web-services';
import { formatTime } from './utils';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  booking: ScheduleEntry['booking'] | null;
  namespace?: string;
  showContactDetails?: boolean;
}

const bookingMapHref = (
  booking: ScheduleEntry['booking'] | null,
): string => {
  const addr = booking?.address;
  if (!addr?.street && !addr?.city) return '#';
  const q = [addr?.street, addr?.city, addr?.state].filter(Boolean).join(', ');
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}`;
};

export const BookingPreviewModal = ({
  isOpen,
  onClose,
  booking,
  namespace,
  showContactDetails,
}: Props): JSX.Element => {
  const { t } = useSharedTranslation(namespace);
  const pending = booking?.status?.code === 'parent_booking'
    || booking?.status?.code === 'parent_reschedule';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        pending
          ? t('pages.calendar.bookingRequest')
          : t('pages.calendar.upcomingBooking')
      }
      className='w-full max-w-md'
    >
      {booking && (
        <>
          {showContactDetails && (
            <div className='flex items-start gap-3 mb-4'>
              {booking.parent?.pictureUrl && (
                <img
                  id={`booking-modal-parent-picture-${booking.parent.id ?? ''}`}
                  alt={t('pages.calendar.profilePhotoFor', { name: booking.parent.name ?? '' })}
                  src={booking.parent.pictureUrl}
                  className='w-20 h-20 rounded object-cover'
                />
              )}
              <div>
                <div className='font-semibold'>{booking.parent?.name}</div>
                {booking.address && (
                  <a
                    href={bookingMapHref(booking)}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='underline text-sm'
                  >
                    {booking.address.street}
                    {booking.address.street && <br />}
                    {booking.address.city}
                    {booking.address.city && booking.address.state && ', '}
                    {booking.address.state}
                  </a>
                )}
              </div>
            </div>
          )}
          <div className='mb-4 text-sm'>
            {t('common.timeRange', { start: formatTime(booking.start), end: formatTime(booking.end) })}
          </div>
          {showContactDetails && (
            <div className='pl-24 mb-4 text-sm'>
              {t('pages.calendar.ratePerHour', { rate: booking.rate })}
              <br />
              {t('pages.calendar.childCount', { count: booking.numberOfChildren ?? 0 })}
              {booking.kidsAges && (
                <span> {t('pages.calendar.kidsAgesParenthetical', { ages: booking.kidsAges })}</span>
              )}
              <br />
              {booking.parent?.phone && (
                <>
                  {booking.parent.phone}
                  <br />
                </>
              )}
              {t('pages.calendar.payVia', { paymentType: booking.paymentType })}
            </div>
          )}
          <a
            id='booking-details-button'
            href={`/app/bookings/${booking.id}`}
            className='btn btn-primary btn-block'
          >
            {pending
              ? t('pages.calendar.viewRequest')
              : t('pages.calendar.viewBooking')}
          </a>
        </>
      )}
    </Modal>
  );
};
